import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Users, Globe, Sun, Moon, Star, Sparkles, Calendar, Clock, MapPin, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../i18n/LanguageContext';
interface HeroBannerProps {
  onNavigate: (page: string) => void;
}
export function HeroBanner({
  onNavigate
}: HeroBannerProps) {
  const {
    t
  } = useLanguage();
  // Memoize star positions so they don't recalculate on every re-render
  const stars = useMemo(() => Array.from({
    length: 50
  }, (_, i) => ({
    id: i,
    width: Math.random() * 2 + 1,
    height: Math.random() * 2 + 1,
    top: Math.random() * 100,
    left: Math.random() * 100,
    opacity: Math.random() * 0.5 + 0.1,
    duration: Math.random() * 4 + 2,
    delay: Math.random() * 2
  })), []);
  return <section className="relative w-full min-h-[85vh] flex flex-col overflow-hidden bg-[#050b14] font-sans pt-16 lg:pt-0" data-id="element-16703">
      {/* Cosmic Background */}
      <div className="absolute inset-0 pointer-events-none z-0" data-id="element-16704">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#112240] via-[#0a1628] to-[#050b14] opacity-90" data-id="element-16705" />

        {/* Stars */}
        {stars.map(s => <div key={s.id} className="absolute rounded-full bg-white" style={{
        width: `${s.width}px`,
        height: `${s.height}px`,
        top: `${s.top}%`,
        left: `${s.left}%`,
        opacity: s.opacity,
        animation: `pulse ${s.duration}s infinite ${s.delay}s`
      }} data-id="element-16706" />)}

        {/* Glowing Orbs */}
        <div className="absolute top-[10%] left-[20%] w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full bg-indigo-600/10 blur-[120px]" data-id="element-16707" />
        <div className="absolute bottom-[20%] right-[10%] w-[350px] sm:w-[600px] h-[350px] sm:h-[600px] rounded-full bg-amber-600/10 blur-[150px]" data-id="element-16708" />
        <div className="absolute top-[40%] right-[30%] w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] rounded-full bg-blue-500/10 blur-[100px]" data-id="element-16709" />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:flex-row relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 items-center pb-16 sm:pb-24 lg:pb-0" data-id="element-16710">
        {/* Left Column - Text & Form */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center pt-6 sm:pt-10 lg:pt-0 z-20" data-id="element-16711">
          {/* Title & Subtitle FIRST */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: 'easeOut'
        }} data-id="element-16712">
            <h1 className="sm:text-2xl md:text-3xl lg:text-[2.5rem] font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-indigo-100 to-slate-300 mb-3 sm:mb-4 tracking-tight text-[42px]" data-id="element-16713">
              {t('hero.title.line1')}
              <br data-id="element-16714" />
              {t('hero.title.line2')}
              <br data-id="element-16715" />
              {t('hero.title.line3')}
            </h1>

            <p className="sm:text-sm lg:text-base text-slate-300/80 mb-6 sm:mb-8 max-w-md font-light text-[14px]" data-id="element-16716">
              {t('hero.subtitle')}
            </p>
          </motion.div>

          {/* Form + Button BELOW */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: 'easeOut'
        }} data-id="element-16717">
            <p className="text-[10px] sm:text-[11px] text-slate-500 mb-3 flex items-center gap-2 uppercase tracking-wider font-medium" data-id="element-16718">
              <Sparkles className="w-3 h-3 text-indigo-400" data-id="element-16719" />
              Заполните данные и рассчитайте карту
            </p>

            {/* Glassmorphism Form */}
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)] w-full max-w-2xl" data-id="element-16720">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-5" data-id="element-16721">
                <div className="flex-1 min-w-0" data-id="element-16722">
                  <label className="flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 sm:mb-2 whitespace-nowrap overflow-hidden" data-id="element-16723">
                    <Calendar className="w-3 h-3 shrink-0" data-id="element-16724" />{' '}
                    <span className="truncate" data-id="element-16725">{t('hero.label.dob')}</span>
                  </label>
                  <input type="text" placeholder={t('hero.placeholder.dob')} className="w-full bg-transparent border-b border-white/20 pb-1.5 sm:pb-2 text-white text-xs sm:text-sm focus:outline-none focus:border-indigo-400 transition-colors placeholder:text-slate-600" data-id="element-16726" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-16727">
                  <label className="flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 sm:mb-2 whitespace-nowrap overflow-hidden" data-id="element-16728">
                    <Clock className="w-3 h-3 shrink-0" data-id="element-16729" />{' '}
                    <span className="truncate" data-id="element-16730">{t('hero.label.tob')}</span>
                  </label>
                  <input type="text" placeholder={t('hero.placeholder.tob')} className="w-full bg-transparent border-b border-white/20 pb-1.5 sm:pb-2 text-white text-xs sm:text-sm focus:outline-none focus:border-indigo-400 transition-colors placeholder:text-slate-600" data-id="element-16731" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-16732">
                  <label className="flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 sm:mb-2 whitespace-nowrap overflow-hidden" data-id="element-16733">
                    <MapPin className="w-3 h-3 shrink-0" data-id="element-16734" />{' '}
                    <span className="truncate" data-id="element-16735">{t('hero.label.pob')}</span>
                  </label>
                  <input type="text" placeholder={t('hero.placeholder.pob')} className="w-full bg-transparent border-b border-white/20 pb-1.5 sm:pb-2 text-white text-xs sm:text-sm focus:outline-none focus:border-indigo-400 transition-colors placeholder:text-slate-600" data-id="element-16736" />
                </div>
              </div>

              {/* Calculate button integrated into form */}
              <div className="mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-white/5" data-id="element-16737">
                <Button className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white border-none rounded-xl px-5 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-bold tracking-wide shadow-[0_0_25px_rgba(99,102,241,0.3)] transition-all hover:shadow-[0_0_35px_rgba(99,102,241,0.5)] w-full" onClick={() => onNavigate('service-natal')} data-id="element-16738">
                  <span className="flex items-center justify-center gap-2" data-id="element-16739">
                    {t('hero.btn.calculate')}
                    <ArrowRight className="w-4 h-4" data-id="element-16740" />
                  </span>
                </Button>
                <p className="text-[10px] text-slate-500 text-center mt-2.5" data-id="element-16741">
                  Бесплатный расчёт · Результат мгновенно
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Visual Chart */}
        <div className="w-full lg:w-1/2 relative flex items-center justify-center min-h-[250px] sm:min-h-[350px] lg:min-h-[480px] mt-6 sm:mt-10 lg:mt-0 z-10" data-id="element-16742">
          <div className="relative w-[220px] h-[220px] sm:w-[320px] sm:h-[320px] lg:w-[420px] lg:h-[420px] xl:w-[460px] xl:h-[460px] flex items-center justify-center" data-id="element-16743">
            {/* Outer Glow */}
            <div className="absolute inset-0 rounded-full bg-indigo-500/5 blur-[100px]" data-id="element-16744" />

            {/* Ring 1 (Outer) */}
            <motion.div className="absolute inset-[5%] rounded-full border border-white/10 border-dashed" animate={{
            rotate: 360
          }} transition={{
            duration: 120,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-16745">
              {/* Planet: Sun */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" data-id="element-16746">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#0a1628] border border-amber-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(245,158,11,0.4)]" data-id="element-16747">
                  <Sun className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400" data-id="element-16748" />
                </div>
                <motion.div className="absolute top-8 sm:top-10 whitespace-nowrap bg-[#0a1628]/80 backdrop-blur-sm border border-white/10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[8px] sm:text-[10px] text-slate-300 font-medium tracking-wide hidden sm:block" animate={{
                rotate: -360
              }} transition={{
                duration: 120,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-16749">
                  {t('hero.planet.sun')}
                </motion.div>
              </div>

              {/* Planet: Ascendant */}
              <div className="absolute bottom-[15%] right-[5%] translate-x-1/2 translate-y-1/2 flex flex-col items-center" data-id="element-16750">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#0a1628] border border-emerald-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.4)]" data-id="element-16751">
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" data-id="element-16752" />
                </div>
                <motion.div className="absolute top-8 sm:top-10 whitespace-nowrap bg-[#0a1628]/80 backdrop-blur-sm border border-white/10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[8px] sm:text-[10px] text-slate-300 font-medium tracking-wide hidden sm:block" animate={{
                rotate: -360
              }} transition={{
                duration: 120,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-16753">
                  {t('hero.planet.asc')}
                </motion.div>
              </div>
            </motion.div>

            {/* Ring 2 (Middle) */}
            <motion.div className="absolute inset-[20%] rounded-full border border-indigo-500/20" animate={{
            rotate: -360
          }} transition={{
            duration: 90,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-16754">
              {/* Zodiac signs markers (decorative) */}
              {[...Array(12)].map((_, i) => {
              const angle = i * 30 * (Math.PI / 180);
              const radius = 50;
              const x = 50 + radius * Math.cos(angle - Math.PI / 2);
              const y = 50 + radius * Math.sin(angle - Math.PI / 2);
              return <div key={i} className="absolute w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-indigo-400/60" style={{
                top: `${y}%`,
                left: `${x}%`,
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 6px rgba(129,140,248,0.4)'
              }} data-id="element-16755" />;
            })}

              {/* Planet: Moon */}
              <div className="absolute top-[20%] right-[5%] translate-x-1/2 -translate-y-1/2 flex flex-col items-center" data-id="element-16756">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#0a1628] border border-blue-400/50 flex items-center justify-center shadow-[0_0_15px_rgba(96,165,250,0.4)]" data-id="element-16757">
                  <Moon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-300" data-id="element-16758" />
                </div>
                <motion.div className="absolute top-8 sm:top-10 whitespace-nowrap bg-[#0a1628]/80 backdrop-blur-sm border border-white/10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[8px] sm:text-[10px] text-slate-300 font-medium tracking-wide hidden sm:block" animate={{
                rotate: 360
              }} transition={{
                duration: 90,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-16759">
                  {t('hero.planet.moon')}
                </motion.div>
              </div>
            </motion.div>

            {/* Ring 3 (Inner) */}
            <motion.div className="absolute inset-[40%] rounded-full border border-white/10 border-dotted" animate={{
            rotate: 360
          }} transition={{
            duration: 60,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-16760">
              {/* Inner geometric lines */}
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100" data-id="element-16761">
                <polygon points="50,0 100,75 0,75" fill="none" stroke="#818cf8" strokeWidth="0.5" data-id="element-16762" />
                <polygon points="50,100 100,25 0,25" fill="none" stroke="#818cf8" strokeWidth="0.5" data-id="element-16763" />
                <line x1="50" y1="0" x2="50" y2="100" stroke="#818cf8" strokeWidth="0.5" data-id="element-16764" />
                <line x1="0" y1="50" x2="100" y2="50" stroke="#818cf8" strokeWidth="0.5" data-id="element-16765" />
              </svg>
            </motion.div>

            {/* Center Core */}
            <div className="absolute inset-[45%] rounded-full bg-gradient-to-tr from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 backdrop-blur-md flex items-center justify-center shadow-[0_0_50px_rgba(99,102,241,0.2)]" data-id="element-16766">
              <Sparkles className="w-5 h-5 sm:w-8 sm:h-8 text-indigo-300 opacity-80" data-id="element-16767" />
              <motion.div className="absolute inset-0 rounded-full border border-indigo-400/50" animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0, 0.5]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-16768" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Statistics Strip */}
      <div className="relative z-20 w-full bg-[#050b14]/80 backdrop-blur-xl border-t border-white/10 py-3 sm:py-4 mt-auto" data-id="element-16769">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-16770">
          <div className="grid grid-cols-3 gap-2 sm:gap-4 divide-x divide-white/10" data-id="element-16771">
            {[{
            icon: <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" data-id="element-16772" />,
            value: '23,000+',
            label: t('hero.stat.charts')
          }, {
            icon: <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-400" data-id="element-16773" />,
            value: '120+',
            label: t('hero.stat.astrologers')
          }, {
            icon: <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" data-id="element-16774" />,
            value: '54',
            label: t('hero.stat.countries')
          }].map((stat, i) => <div key={i} className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 px-1" data-id="element-16775">
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0" data-id="element-16776">
                  {stat.icon}
                </div>
                <div className="text-center sm:text-left" data-id="element-16777">
                  <div className="text-sm sm:text-lg lg:text-xl font-bold text-white tracking-tight leading-none" data-id="element-16778">
                    {stat.value}
                  </div>
                  <div className="text-[7px] sm:text-[9px] lg:text-[10px] text-slate-400 uppercase tracking-wider font-semibold mt-0.5 leading-tight" data-id="element-16779">
                    {stat.label}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}